import type { LearnerCountersRow } from '~/gql/billing/billing'
import { useQuery } from '@apollo/client/react'
import { BillingTier } from '@zeus/index'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { GQL_LEARNER_COUNTERS_BY_ORGANIZATION } from '~/gql/billing/billing'
import { client } from '~/helpers/apollo'
import { neededTierLabel } from '~/helpers/billing/needed-tier'
import { tourAnchor } from '~/helpers/tour/anchors'
import BillingMonthHeader from '~/pages/backoffice/admin/billing/BillingMonthHeader'
import { formatCents } from '~/pages/backoffice/admin/billing/money'
import { currentMonth } from '~/pages/backoffice/admin/billing/month'
import OrgStorageTable from '~/pages/backoffice/admin/billing/OrgStorageTable'

/**
 * Compteurs d'apprenants et palier par organisation (#45, #51, #183).
 *
 * Écran d'OBSERVATION : il ne pilote aucun blocage, et le palier s'y lit sans s'y
 * modifier — c'est l'admin de l'organisation qui le choisit. Le franchissement de
 * borne est signalé, jamais appliqué (BUSINESS_MODEL.md).
 */
function LearnerCountersTable() {
  const { t, i18n } = useTranslation()
  const [month, setMonth] = useState(() => currentMonth())

  const { data, loading } = useQuery(GQL_LEARNER_COUNTERS_BY_ORGANIZATION, {
    client,
    variables: { month },
    fetchPolicy: 'network-only',
  })

  const rows = (data?.learnerCountersByOrganization ?? []) as unknown as LearnerCountersRow[]
  const totalMonthly = rows.reduce((sum, row) => sum + row.monthlyActive, 0)
  const totalLearners = rows.reduce((sum, row) => sum + row.learnerCount, 0)
  const totalRegistered = rows.reduce((sum, row) => sum + row.registeredLearners, 0)
  const totalRevenue = rows.reduce((sum, row) => sum + row.estimatedRevenueCents, 0)

  return (
    <div className="space-y-4">
      <BillingMonthHeader month={month} onChange={setMonth} />

      <div className="rounded-md border" {...tourAnchor('billing.counters')}>
        <Table aria-label={t('billing.learnerCounters.table')}>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.organizations')}</TableHead>
              <TableHead className="text-right">{t('billing.learnerCount')}</TableHead>
              <TableHead className="text-right">{t('billing.monthlyActive')}</TableHead>
              <TableHead className="text-right">{t('billing.registered')}</TableHead>
              <TableHead>{t('billing.plan')}</TableHead>
              <TableHead className="text-right">{t('billing.estimatedRevenue')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.orgId}>
                <TableCell className="font-medium">
                  {row.orgName}
                  <span className="ml-2 text-muted-foreground">{row.orgSlug}</span>
                </TableCell>
                {/* Apprenants distincts sur 12 mois glissants, avec les places incluses
                    qui restent, ou le palier à prendre quand le souscrit est dépassé (#149). */}
                <TableCell className="text-right tabular-nums">
                  {row.learnerCount}
                  {row.overCapacity
                    ? (
                        <span className="ml-2 text-xs font-medium text-warning-text">
                          {t('billing.overCapacityShort', { needed: neededTierLabel(t, row.effectiveTier) })}
                        </span>
                      )
                    : row.placesRemaining != null && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {t('billing.placesShort', { places: row.placesRemaining })}
                      </span>
                    )}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {row.monthlyActive}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {row.registeredLearners}
                </TableCell>
                <TableCell>
                  {t(`billing.tier.${row.billedTier}`)}
                  {/* Ni le gratuit ni un compte géré (#349) n'ont d'échéancier. */}
                  {row.billedTier !== BillingTier.FREE && row.billedTier !== BillingTier.CUSTOM && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {t(`billing.term.${row.paymentTerm}`)}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatCents(row.estimatedRevenueCents, i18n.language)}
                </TableCell>
              </TableRow>
            ))}
            {rows.length > 0 && (
              <TableRow className="font-medium">
                <TableCell>{t('billing.total')}</TableCell>
                <TableCell className="text-right tabular-nums">{totalLearners}</TableCell>
                <TableCell className="text-right tabular-nums">{totalMonthly}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {totalRegistered}
                </TableCell>
                <TableCell />
                <TableCell className="text-right tabular-nums">
                  {formatCents(totalRevenue, i18n.language)}
                </TableCell>
              </TableRow>
            )}
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  {t('billing.empty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Second axe d'observation : les apprenants disent ce que la plateforme
          facture, le stockage dit ce qu'elle coûte (#180). */}
      <OrgStorageTable />
    </div>
  )
}

export default LearnerCountersTable
